import api, { dbg, getAuthHeaders, presignedUrlCache, PRESIGNED_URL_CACHE_TTL, S3_BASE_URL, presignedUrlCircuitBreaker } from './_client';
import { config } from '../config/environment';
import { SUPPORTED_MIME_TYPES as SUPPORTED_FILE_TYPES, MIME_TO_EXTENSIONS, inferMimeType } from '../utils/fileTypes';

// --- Ported from assetApi.js (converted from raw fetch to shared axios client) ---

// Get API base URL from centralized config
const DEFAULT_API_BASE_URL = config.api.baseUrl;

export const DEFAULT_ENDPOINTS = {
  uploadUrl: (roomId, apiBase = DEFAULT_API_BASE_URL) => `${apiBase}/rooms/${roomId}/assets/upload-url`,
  roomAssets: (roomId, apiBase = DEFAULT_API_BASE_URL) => `${apiBase}/rooms/${roomId}/assets`,
  deleteAsset: (roomId, assetKey, apiBase = DEFAULT_API_BASE_URL) => `${apiBase}/rooms/${roomId}/assets/${encodeURIComponent(assetKey)}`,
  assetDownloadUrl: (roomId, filename, apiBase = DEFAULT_API_BASE_URL) => `${apiBase}/rooms/${roomId}/assets/${encodeURIComponent(filename)}`,
};

// Supported file types — imported from canonical fileTypes.js (aliased as SUPPORTED_FILE_TYPES)
// MUST match backend SUPPORTED_TYPES in backend/functions/shared/common.js
export { SUPPORTED_MIME_TYPES as SUPPORTED_FILE_TYPES } from '../utils/fileTypes';

export const FILE_SIZE_LIMITS = {
  'image/jpeg': config.upload.maxFileSize.image,
  'image/png': config.upload.maxFileSize.image,
  'image/webp': config.upload.maxFileSize.image,
  'audio/mpeg': config.upload.maxFileSize.audio,
  'audio/wav': config.upload.maxFileSize.audio,
  'audio/x-wav': config.upload.maxFileSize.audio,
  'audio/wave': config.upload.maxFileSize.audio,
  'video/mp4': config.upload.maxFileSize.video,
  'video/quicktime': config.upload.maxFileSize.video,
  'video/x-m4v': config.upload.maxFileSize.video,
  'video/mov': config.upload.maxFileSize.video,
  'model/gltf-binary': config.upload.maxFileSize.model || 50 * 1024 * 1024,
  'application/octet-stream': config.upload.maxFileSize.model || 50 * 1024 * 1024,
};

export function validateFile(file) {
  const errors = [];

  if (!file) {
    return { isValid: false, errors: ['No file provided'] };
  }

  // Infer MIME type when file.type is empty (drag-drop / mobile)
  const effectiveType = inferMimeType(file);

  if (!SUPPORTED_FILE_TYPES.includes(effectiveType)) {
    errors.push(`Unsupported file type: ${file.type || '(unknown)'}. Supported types: ${SUPPORTED_FILE_TYPES.join(', ')}`);
  }

  const maxSize = FILE_SIZE_LIMITS[effectiveType];
  if (maxSize && file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    errors.push(`File size (${Math.round(file.size / (1024 * 1024))}MB) exceeds ${maxSizeMB}MB limit for ${effectiveType}`);
  }

  if (file.size === 0) {
    errors.push('File is empty');
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Upload asset using AWS S3 presigned URL flow
 * 1. Request presigned URL from backend (via shared axios client)
 * 2. Upload file directly to S3 using presigned URL
 * 3. Return asset metadata
 */
export async function uploadAsset(file, { roomId, apiBaseUrl = DEFAULT_API_BASE_URL } = {}) {
  try {
    if (!roomId) {
      return {
        success: false,
        error: { code: 'MISSING_ROOM_ID', message: 'roomId is required for asset upload' }
      };
    }

    const validation = validateFile(file);
    if (!validation.isValid) {
      return {
        success: false,
        error: { code: 'VALIDATION_FAILED', message: validation.errors.join(', '), details: validation.errors },
      };
    }

    // Step 1: Request presigned URL from backend (uses shared axios client)
    const authHeaders = await getAuthHeaders();
    const uploadUrlEndpoint = `/rooms/${roomId}/assets/upload-url`;
    const urlResponse = await api.post(uploadUrlEndpoint, {
      filename: file.name,
      // Infer MIME type when file.type is empty (drag-drop / mobile browsers)
      contentType: inferMimeType(file),
      fileSize: file.size,
      description: '',
    }, { headers: authHeaders });

    const urlData = urlResponse.data;
    dbg.log('Upload URL response:', urlData);

    if (!urlData.success) {
      throw new Error(urlData.error?.message || 'Failed to get upload URL');
    }

    const { uploadUrl, assetMetadata, assetKey } = urlData.data;
    dbg.log('Upload URL:', uploadUrl?.substring(0, 100) + '...');
    dbg.log('Asset metadata:', assetMetadata);

    // Step 2: Upload file directly to S3 (raw fetch — not through API gateway)
    dbg.log('Starting S3 upload...');
    const s3Response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    dbg.log('S3 upload response:', s3Response.status, s3Response.statusText);

    if (!s3Response.ok) {
      throw new Error(`S3 upload failed (${s3Response.status}): ${s3Response.statusText}`);
    }

    // Step 3: Return success with asset metadata
    const finalData = {
      ...assetMetadata,
      assetKey,
      url: assetMetadata.publicUrl,
      uploadedAt: new Date().toISOString()
    };

    dbg.log('Upload completed successfully! Final data:', finalData);

    return { success: true, data: finalData };

  } catch (err) {
    console.error('Asset upload error:', err);
    return { success: false, error: { code: 'UPLOAD_FAILED', message: err.message } };
  }
}

export async function uploadMultipleAssets(files, opts = {}) {
  const fileArray = Array.from(files || []);
  const uploaded = [];
  const failed = [];

  for (const file of fileArray) {
    const result = await uploadAsset(file, opts);
    if (result.success) {
      uploaded.push(result.data);
    } else {
      failed.push({ filename: file?.name, error: result.error });
    }
  }

  return {
    success: uploaded.length > 0,
    data: { uploaded, failed, summary: { total: fileArray.length, successful: uploaded.length, failed: failed.length } },
  };
}

/**
 * Get a presigned download URL for a room asset.
 * The assets bucket is private — direct S3 URLs return 403.
 */
export async function getAssetDownloadUrl(roomId, filename, { apiBaseUrl = DEFAULT_API_BASE_URL } = {}) {
  if (!roomId || !filename) {
    throw new Error('roomId and filename are required');
  }
  const res = await api.get(`/rooms/${roomId}/assets/${encodeURIComponent(filename)}`);
  return res.data.data.downloadUrl;
}

/**
 * Get all assets for a room
 */
export async function getRoomAssets(roomId, { apiBaseUrl = DEFAULT_API_BASE_URL } = {}) {
  if (!roomId) {
    return { success: false, error: { code: 'MISSING_ROOM_ID', message: 'roomId is required' } };
  }

  try {
    const authHeaders = await getAuthHeaders();
    const res = await api.get(`/rooms/${roomId}/assets`, { headers: authHeaders });
    return { success: true, data: res.data.data || res.data };
  } catch (err) {
    console.error('Get room assets error:', err);
    return { success: false, error: { code: 'FETCH_FAILED', message: err.message } };
  }
}

/**
 * Delete a room asset
 */
export async function deleteAsset(assetKey, { roomId, apiBaseUrl = DEFAULT_API_BASE_URL } = {}) {
  if (!roomId || !assetKey) {
    return { success: false, error: { code: 'MISSING_PARAMETERS', message: 'roomId and assetKey are required' } };
  }

  try {
    const authHeaders = await getAuthHeaders();
    const res = await api.delete(`/rooms/${roomId}/assets/${encodeURIComponent(assetKey)}`, { headers: authHeaders });
    return { success: true, data: res.data.data || { assetKey, deletedAt: new Date().toISOString() } };
  } catch (err) {
    console.error('Delete asset error:', err);
    return { success: false, error: { code: 'DELETE_FAILED', message: err.message } };
  }
}

// --- End ported functions ---

const assetsApi = {
  // Get available models from API (S3 scan) or local data as fallback
  getAvailableModels: async () => {
    try {
      // First try to get live models from S3 scan endpoint
      dbg.log('Attempting to fetch models from S3 scan endpoint...');
      const response = await api.get('/assets/models/scan');
      dbg.log('S3 scan response:', response);

      const models = response.data?.data?.models || response.data?.models;
      dbg.log('Parsed models from S3 scan:', models?.length, 'models');

      if (models && models.length > 0) {
        dbg.log(`✅ Loaded ${models.length} models from S3 scan`);
        return models;
      } else {
        console.warn('S3 scan returned empty models array');
      }

    } catch (apiError) {
      console.error('❌ Failed to fetch models from S3 scan API:', apiError);
      // No fallback - return empty array if API fails
      return [];
    }
  },

  // Get hierarchical asset data from API (includes models and hierarchical categories)
  getAssetsWithHierarchy: async () => {
    try {
      dbg.log('Attempting to fetch assets with hierarchy from S3 scan endpoint...');
      const response = await api.get('/assets/models/scan');
      dbg.log('S3 scan response for hierarchy:', response);

      const data = response.data?.data || response.data;
      const hierarchicalCategories = data?.hierarchicalCategories;
      const models = data?.models || [];

      dbg.log('Parsed assets:', {
        modelsCount: models?.length || 0,
        hierarchicalCategories: hierarchicalCategories ? Object.keys(hierarchicalCategories).length : 0
      });

      if (hierarchicalCategories) {
        dbg.log(`✅ Loaded hierarchical assets with ${Object.keys(hierarchicalCategories).length} top-level categories`);
        return {
          hierarchicalCategories,
          models,
          categories: data?.categories,
          categorizedModels: data?.categorizedModels,
          total: data?.total || models?.length || 0
        };
      } else {
        console.warn('S3 scan returned no hierarchical categories, falling back to flat structure');
        return {
          hierarchicalCategories: null,
          models,
          total: models?.length || 0
        };
      }

    } catch (apiError) {
      console.error('❌ Failed to fetch assets with hierarchy from S3 scan API:', apiError);
      return {
        hierarchicalCategories: null,
        models: [],
        total: 0
      };
    }
  },

  // Get model categories
  getModelCategories: async () => {
    try {
      const response = await api.get('/assets/categories');
      return response.data;
    } catch (error) {
      console.warn('Using fallback categories - API unavailable');
      // Fallback categories
      return {
        categories: [
          { name: 'chair', count: 5, formats: ['glb'] },
          { name: 'table', count: 3, formats: ['glb'] },
          { name: 'couch', count: 2, formats: ['glb'] },
          { name: 'lamp', count: 4, formats: ['glb'] },
          { name: 'plant', count: 3, formats: ['glb'] },
          { name: 'decoration', count: 6, formats: ['glb'] }
        ],
        total: 6
      };
    }
  },

  // Get presigned URL for a model (can accept model object or path string)
  getModelUrl: async (modelOrPath) => {
    try {
      // If it's a model object, extract the ID or path
      let modelId;
      if (typeof modelOrPath === 'object') {
        modelId = modelOrPath.id || modelOrPath.assetId;
      } else {
        modelId = modelOrPath;
      }

      if (!modelId) {
        console.warn('No model ID provided for presigned URL');
        return null;
      }

      // Check cache first
      const cached = presignedUrlCache.get(modelId);
      if (cached && Date.now() < cached.expiresAt) {
        dbg.log(`📦 Using cached presigned URL for ${modelId}`);
        return cached.url;
      }

      dbg.log(`🔗 Requesting presigned URL for model: ${modelId}`);

      // Request presigned URL from backend (uses dedicated circuit breaker)
      const response = await presignedUrlCircuitBreaker.execute(
        () => api.get(`/assets/models/${modelId}/presigned-url`)
      );
      const presignedUrl = response.data.data?.presignedUrl || response.data.presignedUrl;

      if (!presignedUrl) {
        console.warn('❌ Backend returned no presigned URL for model:', modelId);
        return null;
      }

      // Cache the URL
      presignedUrlCache.set(modelId, {
        url: presignedUrl,
        expiresAt: Date.now() + PRESIGNED_URL_CACHE_TTL
      });

      dbg.log(`✅ Got presigned URL from backend for ${modelId}`);
      dbg.log('🔗 URL preview:', presignedUrl.substring(0, 100) + '...');

      return presignedUrl;

    } catch (error) {
      console.error('❌ Failed to get presigned URL for model:', modelOrPath);
      console.error('🔍 API Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });

      // If it's a network error or 5xx error, suggest using fallback
      if (!error.response || error.response.status >= 500) {
        console.warn('📞 Network/Server error - frontend should use fallback URL if available');
      }

      return null; // No fallback since S3 bucket is private
    }
  },

  // Get batch presigned URLs for multiple models (single API call)
  getBatchPresignedUrls: async (modelIds) => {
    try {
      if (!Array.isArray(modelIds) || modelIds.length === 0) {
        console.warn('No model IDs provided for batch presigned URLs');
        return {};
      }

      // Filter out already cached modelIds
      const uncachedIds = modelIds.filter(id => {
        const cached = presignedUrlCache.get(id);
        return !cached || Date.now() >= cached.expiresAt;
      });

      // Build result from cache for already cached items
      const result = {};
      modelIds.forEach(id => {
        const cached = presignedUrlCache.get(id);
        if (cached && Date.now() < cached.expiresAt) {
          result[id] = { presignedUrl: cached.url };
        }
      });

      // If all items are cached, return early
      if (uncachedIds.length === 0) {
        dbg.log(`📦 All ${modelIds.length} presigned URLs served from cache`);
        return result;
      }

      dbg.log(`🔗 Requesting batch presigned URLs for ${uncachedIds.length} models (${modelIds.length - uncachedIds.length} cached)`);

      // Request batch presigned URLs from backend for uncached items only (uses dedicated circuit breaker)
      const response = await presignedUrlCircuitBreaker.execute(
        () => api.post('/assets/models/presigned-urls', {
          modelIds: uncachedIds,
          expiresIn: 3600
        })
      );

      const data = response.data.data || response.data;
      const urls = data?.urls || {};

      // Cache the new URLs and add to result
      Object.entries(urls).forEach(([modelId, urlData]) => {
        if (urlData?.presignedUrl) {
          presignedUrlCache.set(modelId, {
            url: urlData.presignedUrl,
            expiresAt: Date.now() + PRESIGNED_URL_CACHE_TTL
          });
          result[modelId] = urlData;
        }
      });

      dbg.log(`✅ Got batch presigned URLs for ${Object.keys(urls).length} models`);

      return result;
    } catch (error) {
      console.error('❌ Failed to get batch presigned URLs:', error);
      console.error('🔍 API Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });

      // Return empty object on error
      return {};
    }
  },

  // Get both model and thumbnail presigned URLs
  getModelWithThumbnailUrls: async (modelOrPath) => {
    try {
      // If it's a model object, extract the ID or path
      let modelId;
      if (typeof modelOrPath === 'object') {
        modelId = modelOrPath.id || modelOrPath.assetId;
      } else {
        modelId = modelOrPath;
      }

      if (!modelId) {
        console.warn('No model ID provided for presigned URLs');
        return { modelUrl: null, thumbnailUrl: null };
      }

      dbg.log(`🔗 Requesting presigned URLs for model: ${modelId}`);

      // Request presigned URLs from backend (uses dedicated circuit breaker)
      const response = await presignedUrlCircuitBreaker.execute(
        () => api.get(`/assets/models/${modelId}/presigned-url`)
      );
      const data = response.data.data || response.data;

      const modelUrl = data?.presignedUrl;
      const thumbnailUrl = data?.thumbnailPresignedUrl;

      dbg.log(`✅ Got URLs from backend for ${modelId}:`, {
        hasModelUrl: !!modelUrl,
        hasThumbnailUrl: !!thumbnailUrl
      });

      return {
        modelUrl,
        thumbnailUrl,
        asset: data?.asset
      };

    } catch (error) {
      console.error('❌ Failed to get presigned URLs for model:', modelOrPath);
      console.error('🔍 API Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });

      return { modelUrl: null, thumbnailUrl: null };
    }
  },

  // Get thumbnail URL for a model
  getThumbnailUrl: (modelPath) => {
    return `${S3_BASE_URL}/template/thumbnails/${modelPath.replace('/', '_')}.jpg`;
  },

  // Preload model (for caching)
  preloadModel: async (modelPath) => {
    try {
      const url = await assetsApi.getModelUrl(modelPath);
      if (!url) {
        console.warn('No URL available for model:', modelPath);
        return false;
      }
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Failed to preload model:', modelPath, error);
      return false;
    }
  }
};


export { assetsApi };
